import { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { DtsenField } from '@/hooks/use-bnba-datasets';

interface FieldPickerProps {
  fields: DtsenField[];
  selected: string[];
  /** Dictionary fields the chosen upload does not carry. */
  missing: string[];
  /** Shown before each served column name, for fields of the partner set. */
  prefix?: string;
  onChange: (selected: string[]) => void;
}

export function SensitivityBadge({ sensitivity }: { sensitivity: DtsenField['sensitivity'] }) {
  if (sensitivity === 'pii') return <Badge variant="destructive">PII</Badge>;
  if (sensitivity === 'quasi-id') return <Badge variant="warning">quasi-id</Badge>;
  return null;
}

/** Checkbox list of one set's fields, grouped as in the column dictionary. */
export function FieldPicker({ fields, selected, missing, prefix = '', onChange }: FieldPickerProps) {
  const groups = useMemo(() => {
    const byGroup = new Map<string, DtsenField[]>();
    for (const field of fields) {
      byGroup.set(field.group, [...(byGroup.get(field.group) ?? []), field]);
    }
    return Array.from(byGroup.entries());
  }, [fields]);

  const chosen = new Set(selected);
  const unavailable = new Set(missing);

  const toggle = (name: string, on: boolean) => {
    const next = new Set(chosen);
    if (on) next.add(name);
    else next.delete(name);
    onChange(Array.from(next));
  };

  const toggleGroup = (members: DtsenField[], on: boolean) => {
    const next = new Set(chosen);
    for (const f of members) {
      if (unavailable.has(f.name)) continue;
      if (on) next.add(f.name);
      else next.delete(f.name);
    }
    onChange(Array.from(next));
  };

  return (
    <div className="space-y-4">
      {groups.map(([group, members]) => {
        const available = members.filter((f) => !unavailable.has(f.name));
        const count = available.filter((f) => chosen.has(f.name)).length;
        const all = available.length > 0 && count === available.length;
        return (
          <div key={group} className="rounded-lg border">
            <div className="flex items-center justify-between border-b bg-surface-focus/50 px-3 py-2">
              <p className="text-sm font-semibold">
                {group}{' '}
                <span className="font-normal text-muted-foreground">
                  {count}/{members.length}
                </span>
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={available.length === 0}
                onClick={() => toggleGroup(members, !all)}
              >
                {all ? 'Kosongkan' : 'Pilih semua'}
              </Button>
            </div>
            <div className="grid gap-x-4 gap-y-1 p-3 md:grid-cols-2">
              {members.map((field) => {
                const id = `field-${field.set}-${field.name}`;
                const absent = unavailable.has(field.name);
                return (
                  <label
                    key={field.name}
                    htmlFor={id}
                    className={`flex items-start gap-2 rounded-md px-2 py-1.5 text-sm ${
                      absent ? 'opacity-50' : 'cursor-pointer hover:bg-surface-focus/60'
                    }`}
                  >
                    <Checkbox
                      id={id}
                      className="mt-0.5"
                      checked={chosen.has(field.name)}
                      disabled={absent}
                      onCheckedChange={(v) => toggle(field.name, v === true)}
                    />
                    <span className="min-w-0 space-y-0.5">
                      <span className="flex flex-wrap items-center gap-1.5">
                        <code className="break-all text-xs font-medium">
                          {prefix}
                          {field.name}
                        </code>
                        <SensitivityBadge sensitivity={field.sensitivity} />
                        {absent ? <Badge variant="outline">tidak ada di berkas</Badge> : null}
                      </span>
                      {field.definition ? (
                        <span className="block text-xs text-muted-foreground">
                          {field.definition}
                        </span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
